// The set editor's form state: `useSetForm` (validation, API field errors, dirty tracking) and unsaved edits kept in
// sessionStorage (`setDraft:<id|new>`, written on `bda:session-expired`; design "Unsaved edits are kept in
// sessionStorage").
import { useEffect, useMemo, useState } from 'react';
import {
  DEFAULT_COVERAGE_FLOOR,
  EMPTY_SUMMARY_RULES,
  validateSetDefinition,
  type SetMemberRef,
  type SummaryRules,
  type ValidationErrors,
} from '@bda/shared';
import { ApiError } from '../../api/client';
import { SESSION_EXPIRED_EVENT } from '../../auth/auth';
import { type SetMembersValidation } from './SetMembersEditor';
import { useNotify } from '../../lib/notify';
import { coverageFloorText, parseCoverageFloor } from './coverageFloor';
import type { BlueprintSet } from './setShared';

// ---------- unsaved edits (design "Unsaved edits are kept in sessionStorage") ----------

export interface SetDraftState {
  name: string;
  description: string;
  members: SetMemberRef[];
  summaryRules: SummaryRules;
  /** The "Coverage floor" input text, in percent. */
  floorText: string;
}

export const setDraftKey = (id: string | undefined): string => `setDraft:${id ?? 'new'}`;

export function saveSetDraft(id: string | undefined, state: SetDraftState, revision: number | null): void {
  window.sessionStorage.setItem(setDraftKey(id), JSON.stringify({ state, revision, at: Date.now() }));
}

export function loadSetDraft(id: string | undefined): { state: SetDraftState; revision: number | null } | null {
  const raw = window.sessionStorage.getItem(setDraftKey(id));
  if (!raw) return null;
  try {
    const v = JSON.parse(raw) as { state?: SetDraftState; revision?: number | null };
    if (!v.state || !Array.isArray(v.state.members) || typeof v.state.name !== 'string') return null;
    return {
      state: { ...v.state, summaryRules: v.state.summaryRules ?? { ...EMPTY_SUMMARY_RULES } },
      revision: v.revision ?? null,
    };
  } catch {
    return null;
  }
}

export const clearSetDraft = (id: string | undefined): void => window.sessionStorage.removeItem(setDraftKey(id));

export const stateOf = (
  s: Pick<BlueprintSet, 'name' | 'description' | 'members' | 'summaryRules' | 'coverageFloor'>,
): SetDraftState => ({
  name: s.name,
  description: s.description,
  members: s.members,
  summaryRules: s.summaryRules,
  floorText: coverageFloorText(s.coverageFloor),
});
export const emptyState = (): SetDraftState => ({
  name: '',
  description: '',
  members: [],
  summaryRules: { headerFields: [], rollups: [], checks: [] },
  floorText: coverageFloorText(DEFAULT_COVERAGE_FLOOR),
});
const sameState = (a: SetDraftState, b: SetDraftState): boolean => JSON.stringify(a) === JSON.stringify(b);

const MEMBER_KEY = /^members(\.|$)/;
const NOT_RULE_KEYS = new Set(['name', 'description', 'coverageFloor']);
/** Tail of `validateSetDefinition`'s `forPublish` message for a Member at its draft. */
const PUBLISH_ONLY_MESSAGE = 'A set version uses published blueprint versions only.';

/**
 * State, validation, API errors, and the session-expired mirror shared by the create and detail pages. `id` is the set
 * (undefined: a new set); `basis` is what is saved.
 */
export function useSetForm(id: string | undefined, basis: SetDraftState, basisRevision: number | null) {
  const notify = useNotify();
  const [initial] = useState(() => {
    const draft = loadSetDraft(id);
    return draft
      ? { state: draft.state, revision: draft.revision ?? basisRevision, restored: true }
      : { state: basis, revision: basisRevision, restored: false };
  });
  const [state, setState] = useState<SetDraftState>(initial.state);
  const [saved, setSaved] = useState<SetDraftState>(basis);
  const [revision, setRevision] = useState<number | null>(initial.revision);
  const [showErrors, setShowErrors] = useState(false);
  const [memberValidation, setMemberValidation] = useState<SetMembersValidation>({
    errors: {},
    warnings: [],
    pending: true,
  });
  /** `fieldErrors` of the last 400, a new object per 400, for the Members editor (it hides stale ones itself). */
  const [memberApiErrors, setMemberApiErrors] = useState<Record<string, string> | undefined>();
  /** The other keys of the last 400 (name, description, coverageFloor, rule keys), dropped as the author edits them. */
  const [otherApiErrors, setOtherApiErrors] = useState<ValidationErrors>({});
  const dirty = !sameState(state, saved);

  useEffect(() => {
    if (initial.restored) notify.info('Restored your unsaved changes.');
    // Only on mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // The session could not be refreshed: keep the edits for when sign-in returns to this route.
  useEffect(() => {
    const onExpired = () => {
      if (dirty) saveSetDraft(id, state, revision);
    };
    window.addEventListener(SESSION_EXPIRED_EVENT, onExpired);
    return () => window.removeEventListener(SESSION_EXPIRED_EVENT, onExpired);
  }, [id, state, revision, dirty]);

  const dropApiErrors = (pred: (key: string) => boolean) =>
    setOtherApiErrors((prev) =>
      Object.keys(prev).some(pred) ? Object.fromEntries(Object.entries(prev).filter(([k]) => !pred(k))) : prev,
    );
  const update = <K extends keyof SetDraftState>(key: K, value: SetDraftState[K]) => {
    setState((s) => ({ ...s, [key]: value }));
    if (key === 'name' || key === 'description') dropApiErrors((k) => k === key);
    if (key === 'floorText') dropApiErrors((k) => k === 'coverageFloor');
    if (key === 'summaryRules') dropApiErrors((k) => !NOT_RULE_KEYS.has(k) && !MEMBER_KEY.test(k));
  };
  const applyApiErrors = (fieldErrors: Record<string, string>) => {
    setMemberApiErrors({ ...fieldErrors });
    setOtherApiErrors(Object.fromEntries(Object.entries(fieldErrors).filter(([k]) => !MEMBER_KEY.test(k))));
  };
  const markSaved = (s: BlueprintSet) => {
    clearSetDraft(id);
    const next = stateOf(s);
    setSaved(next);
    setState(next);
    setRevision(s.revision);
    setShowErrors(false);
  };

  const floor = parseCoverageFloor(state.floorText);
  const def = validateSetDefinition({ name: state.name, description: state.description }, [], {
    maxMembers: 1,
    forPublish: false,
  }).errors;
  const nameError = (showErrors || state.name !== '' ? def.name : undefined) ?? otherApiErrors.name;
  const descriptionError = def.description ?? otherApiErrors.description;
  const floorError = 'error' in floor ? floor.error : otherApiErrors.coverageFloor;
  const rulesApiErrors = useMemo(
    () => Object.fromEntries(Object.entries(otherApiErrors).filter(([k]) => !NOT_RULE_KEYS.has(k))),
    [otherApiErrors],
  );
  // A Member at its draft blocks a publish only (Req 3.2): saving the draft with it is fine.
  const memberBlocking = Object.values(memberValidation.errors).some((msg) => !msg.includes(PUBLISH_ONLY_MESSAGE));
  /** Browser-side problems that block a save (warnings do not). */
  const blocking = !!def.name || !!def.description || 'error' in floor || memberValidation.pending || memberBlocking;

  return {
    state,
    update,
    dirty,
    revision,
    showErrors,
    setShowErrors,
    memberValidation,
    setMemberValidation,
    memberApiErrors,
    rulesApiErrors,
    applyApiErrors,
    markSaved,
    floor,
    nameError,
    descriptionError,
    floorError,
    blocking,
  };
}

/** `details.fieldErrors` of a 400, or null when the error is not a validation failure. */
export function fieldErrorsOf(e: unknown): Record<string, string> | null {
  if (!(e instanceof ApiError) || e.code !== 'VALIDATION_FAILED') return null;
  const fe = e.details?.fieldErrors;
  return fe && typeof fe === 'object' ? (fe as Record<string, string>) : {};
}
